'use client';

import type { ClassValue } from '@vs-shop/utils';
import type { ReactNode } from 'react';

import { MinusIcon, PlusIcon } from '@heroicons/react/24/solid';
import { clsxm } from '@vs-shop/utils';
import { cva, type VariantProps } from 'class-variance-authority';

export const productAmountCounterVariants = cva(
  [
    'ring-foreground/20 flex overflow-hidden rounded-lg ring-1 ring-inset',
    'h-full w-[100px] @[146px]/counter:w-[146px]',
  ],
  {
    variants: {
      variant: {
        /** Takes the height of the box it is put in, and nothing else. */
        plain: '',
        /**
         * Keeps a minimum height that grows with its own width, for a control
         * standing in a row of its own rather than inside a sized panel.
         */
        tall: 'min-h-[42px] @[146px]/counter:min-h-[55px]',
      },
      loading: {
        true: 'pointer-events-none opacity-50',
        false: '',
      },
    },
    defaultVariants: {
      loading: false,
    },
  }
);

type ProductAmountCounterVariantName = NonNullable<
  VariantProps<typeof productAmountCounterVariants>['variant']
>;

export type ProductAmountCounterVariant = ProductAmountCounterVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductAmountCounterVariant = {
  PLAIN: 'plain',
  TALL: 'tall',
} as const satisfies Record<string, ProductAmountCounterVariantName>;

const productAmountCounterButtonVariants = cva(
  'flex min-h-full flex-1 items-center justify-center',
  {
    variants: {
      disabled: {
        true: 'bg-background-inverted/5 text-foreground-muted cursor-not-allowed',
        false: 'bg-background-inverted/10 text-foreground',
      },
    },
    defaultVariants: {
      disabled: false,
    },
  }
);

export type ProductAmountCounterProps = {
  /**
   * How the control sizes itself. Required rather than defaulted: the two
   * shapes are alternatives, and a caller that omits one gets whichever the
   * recipe happened to list first.
   */
  variant: ProductAmountCounterVariant;
  amount: number;
  onDecrementAction: () => void;
  onIncrementAction: () => void;
  onInputChangeAction: (value: string) => void;
  decrementDisabled: boolean;
  incrementDisabled: boolean;
  inputDisabled?: boolean;
  isLoading: boolean;
  /** Accessible label for the decrement button (host supplies the translation). */
  decrementLabel: string;
  /** Accessible label for the increment button. */
  incrementLabel: string;
  /** Accessible label for the amount input. */
  amountLabel: string;
  className?: ClassValue;
};

export const ProductAmountCounter = ({
  className,
  variant,
  isLoading,
  amount,
  onDecrementAction,
  onIncrementAction,
  onInputChangeAction,
  decrementDisabled,
  incrementDisabled,
  inputDisabled = false,
  decrementLabel,
  incrementLabel,
  amountLabel,
}: ProductAmountCounterProps) => {
  return (
    // `@container/counter` makes this element an inline-size container, and a
    // container's own width is settled by its parent rather than by what is
    // inside it. In a flex row that offers no width — the cart drawer's
    // quantity row — the element therefore measures 0 and the control inside it
    // paints over its neighbour. The floor is the narrow control's own width,
    // so the container never reports less than what it holds.
    <div data-slot="product-amount-counter" className="@container/counter h-full min-w-[100px]">
      <div
        className={clsxm(productAmountCounterVariants({ loading: isLoading, variant }), className)}
      >
        <ProductAmountCounterButton
          dataSlot="product-amount-counter-decrement"
          onClick={onDecrementAction}
          ariaLabel={decrementLabel}
          isDisabled={decrementDisabled}
        >
          <MinusIcon className="w-5 shrink-0" />
        </ProductAmountCounterButton>

        <input
          disabled={inputDisabled}
          required
          type="text"
          name="product-quantity"
          value={amount}
          onChange={(e) => {
            onInputChangeAction(e.target.value);
          }}
          aria-label={amountLabel}
          data-slot="product-amount-counter-input"
          className={clsxm(
            'font-heading',
            'flex flex-1 text-center',
            'w-8 @[146px]/counter:w-10',
            'text-lg/6 @[146px]/counter:text-xl',
            'tracking-extra-wide text-foreground shrink-0 border-0 bg-transparent font-bold uppercase'
          )}
        />

        <ProductAmountCounterButton
          dataSlot="product-amount-counter-increment"
          onClick={onIncrementAction}
          ariaLabel={incrementLabel}
          isDisabled={incrementDisabled}
        >
          <PlusIcon className="w-5 shrink-0" />
        </ProductAmountCounterButton>
      </div>
    </div>
  );
};

function ProductAmountCounterButton({
  dataSlot,
  onClick,
  ariaLabel,
  isDisabled,
  children,
}: {
  dataSlot: string;
  onClick: () => void;
  ariaLabel: string;
  isDisabled: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      disabled={isDisabled}
      data-slot={dataSlot}
      className={clsxm(productAmountCounterButtonVariants({ disabled: isDisabled }))}
    >
      {children}
    </button>
  );
}
